import styled from '@emotion/styled';
import { InsightType } from 'utils/entities';
import { SortDirection } from './index';
const EXPANDABLE_SIDE_PADDING = 30;
const COLUMN_X_PADDING = 8;
const COLUMN_Y_PADDING = 12;

export const TableWrapper = styled.div<{ sidePadding?: number }>`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: ${({ sidePadding = 0 }) => `0 ${sidePadding}px`};
`;

export const TableHeader = styled.div<{ isExpandable?: boolean }>`
  display: flex;
  flex-grow: 0;
  flex-basis: 100%;
  & > .table-row {
    min-height: 44px;
    ${({ isExpandable, theme }) => isExpandable ? `
      padding-${theme.isRTL ? 'left' : 'right'}: ${EXPANDABLE_SIDE_PADDING}px;
    ` : ''}
    ${({ theme }) => theme.media.showAt({ at: 1 }) `
      height: 44px;
    `}
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  }
  .table-cell {
    cursor: pointer;
    font-weight: bold;
  }
`;

export const TableBody = styled.div<{ isExpandable?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  flex-grow: 0;
  flex-basis: 100%;
  width: 100%;
  & .table-row {
    border-bottom: 1px dashed ${({ theme }) => theme.colors.neutrals.grey8};
  }
  ${({ isExpandable }) => isExpandable ? `
    & .table-row {
      cursor: pointer;
    }
    & .table-cell {
      align-items: flex-start;
    }
  ` : ''}
`;

export const TableRow = styled.div<{ wrapColumnText?: boolean; isExpandable?: boolean; isFixed?: boolean; isExpanded?: boolean, alwaysShowExpandIcon?: boolean; isActive?: boolean; type?: InsightType; wrapItems?: boolean; }>`
  display: flex;
  ${({ wrapItems }) => wrapItems ? 'flex-wrap: wrap' : ''};
  flex-grow: 0;
  flex-basis: 100%;
  width: 100%;
  ${({ wrapColumnText }) => wrapColumnText ? '' : `white-space: nowrap;`}
  position: relative;
  ${({ theme, isFixed }) => isFixed ? `
    z-index: 1;
    font-weight: bold;
    pointer-events: none;

    .table-cell {
      color: ${theme.colors.neutrals.grey1};
    }

    a, div > a > div, div {
      font-weight: bold;
    }

    &:hover {
      z-index: 2;
    }
  ` : ''}
  ${({ theme, isExpandable, isExpanded, alwaysShowExpandIcon }) => isExpandable ? `
    padding-${theme.isRTL ? 'left' : 'right'}: ${EXPANDABLE_SIDE_PADDING}px;
    transition: all .3s ease;

    &:after {
      content: '';
      position: absolute;
      border-radius: ${theme.borderRadius.tiny};
      top: -1px;
      left: -${COLUMN_X_PADDING}px;
      right: -${COLUMN_X_PADDING}px;
      bottom: -1px;
      transition: all .3s ease;
      pointer-events: none;
      border-top: 1px solid rgba(255, 255, 255, 0);
    }

    .expand-toggler > svg {
      transition: all .3s ease;
      opacity: ${alwaysShowExpandIcon ? 1 : 0};
      position: absolute;
      ${theme.isRTL ? 'left' : 'right'}: 0;
      top: 10px;
      path {
        fill: ${theme.colors.neutrals.grey5};
      }
      ${isExpanded ? 'transform: rotate(180deg);' : ''}
    }

    ${isExpanded ? `
      border-color: rgba(255, 255, 255, 0) !important;
      &:after {
        border-top: 1px solid #fff;
        box-shadow: ${theme.shadow.level1};
      }
      .expand-toggler > svg {
        opacity: 1;
      }
      .table-cell {
        color: ${theme.colors.neutrals.grey1};
      }
    ` : `
      &:hover {
        border-color: rgba(255, 255, 255, 0);
        &:after {
          border-top: 1px solid #fff;
          box-shadow: ${theme.shadow.level1};
        }
        .expand-toggler > svg {
          opacity: 1;
        }
      }
    `}
  ` : ''}

  ${({ theme, isExpandable }) => theme.media.showAt({ to: 5, isTouch: true }) `
    ${isExpandable ? `
      > .expand-toggler > svg {
        opacity: 1;
      }
    ` : ''}
  `}

    .agent-history {
      flex-shrink: 0;
    }

  ${({ theme, isActive }) => isActive ? `
    background-color: ${theme.colors.neutrals.grey10};
    transition: background-color 200ms;
  ` : ''}
`;

export const TableColumn = styled.div<{ width: string; align?: 'left' | 'right'; gap?: number; minWidth?: string; }>`
  display: inline-flex;
  align-items: center;
  flex-grow: 1;
  padding: ${COLUMN_Y_PADDING}px 0;
  &:not(:last-of-type) {
    padding-${({ theme }) => theme.isRTL ? 'left' : 'right'}: ${({ gap }) => gap !== undefined ? gap : COLUMN_X_PADDING}px;
  }
  color: ${({ theme }) => theme.colors.neutrals.grey3};
  flex-basis: ${({ width }) => width};
  width: ${({ width }) => width};
  ${({ minWidth }) => minWidth ? `min-width: ${minWidth};` : ''};
  flex-direction: row;
  ${({ theme, align }) => theme.media.showAt({ at: 1 })`
    flex-direction: ${align === 'left' || !align ? 'row' : 'row-reverse'};
  `}
`;

export const TableHeadColumn = styled.div<{ align?: 'left' | 'right'; }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutrals.grey3};
  flex-direction: ${({ align }) => align === 'left' || !align ? 'row' : 'row-reverse'};
`;

export const TableSortIconWrapper = styled.div<{ direction: SortDirection, type?: InsightType }>`
  ${({ theme, direction, type }) => `
    height: 22px;
    display: inline-block;
    margin: 0 ${theme.spacing(0.5)};
    transform: scaleY(${direction === 'asc' ? '' : '-'}1);

    svg {
      path {
       fill: ${type ? theme.colors.brand.main : theme.colors.neutrals.grey3}
      }
    }
  `}
`;

export const XScrollableTableWrapper = styled.div<{ fixedTableWidth: string, xScrollableExtendedWidth?: number }>`
  display: flex;
  & .table {
    min-width: 0;
    flex-grow: 0;
    flex-shrink: 0;
  }
  & > .table {
    padding-${({ theme }) => theme.isRTL ? 'left' : 'right'}: 0;
    border-${({ theme }) => theme.isRTL ? 'left' : 'right'}: 2px solid ${({ theme }) => theme.colors.neutrals.grey8};
    width: ${({ fixedTableWidth }) => fixedTableWidth};
    position: relative;
    z-index: 2;
    &:after {
      position: absolute;
      ${({ theme }) => theme.isRTL ? 'right' : 'left'}: 100%;
      width: 12px;
      background: linear-gradient(to ${({ theme }) => theme.isRTL ? 'left' : 'right'}, ${({ theme }) => theme.colors.neutrals.grey8}, rgba(255, 255, 255, 0));
      top: 0;
      bottom: 0;
      content: '';
    }
    .table-row {
      min-width: 0;
      &:before {
        ${({ theme }) => theme.isRTL ? 'left' : 'right'}: 0;
      }
    }
    .tbody {
      min-width: 0;
    }
  }
  & > div {
    width: ${({ fixedTableWidth, xScrollableExtendedWidth = 0 }) => `calc(100% - ${fixedTableWidth} + ${xScrollableExtendedWidth}px)`};
    margin-${({ theme }) => theme.isRTL ? 'left' : 'right'}: -${({ xScrollableExtendedWidth = 0 }) => xScrollableExtendedWidth}px;
  }
`;

export const LoaderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
`;

export const XScrollableTable = styled.div<{ scrollTableWidth: string; }>`
  display: flex;
  min-width: 0;
  flex-grow: 0;
  flex-shrink: 0;
  overflow-x: auto;
  .table {
    padding-${({ theme }) => theme.isRTL ? 'right' : 'left'}: 0;
    width: ${({ scrollTableWidth }) => scrollTableWidth};
  }
  .table .table-row {
    padding-${({ theme }) => theme.isRTL ? 'right' : 'left'}: 16px;
    &:before {
      ${({ theme }) => theme.isRTL ? 'right' : 'left'}: 0;
    }
  }
`;

export const RowsWrapper = styled.div`
  display: flex;
  flex-basis: 100%;
  width: 100%;
  flex-direction: row;
`;

export const ExpandRowWrapper = styled.div`
  min-width: 0;
  width: 100%;
`;
